import { formatMonetary } from "./mask";

export const isMonetary = (value) => typeof value === 'string' && value.trim().replace('-', '').startsWith('R$');
export const isHour = (value) => typeof value === 'string' && /^\d{1,}:\d{2}$/.test(value);
export const isDate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(value);
export const isNumber = (value) => {
    if (typeof value === 'number') return true;
    if (typeof value !== 'string') return false;

    // Remover espaços em branco no início e no final
    const trimmedValue = value.trim();

    // Substituir vírgulas por pontos
    const normalizedValue = trimmedValue.replace(',', '.');

    // Verificar se o valor normalizado é um número
    const isNum = !isNaN(normalizedValue) && !isNaN(parseFloat(normalizedValue));

    // Verificação adicional para formatos numéricos válidos
    const isValidFormat = /^-?\d+(\.\d+)?$/.test(normalizedValue);

    return isNum && isValidFormat;
};

function determineType(element) {
    if (isMonetary(element)) {
        return 'monetary';

    } else if (isHour(element)) {
        return 'hour';

    } else if (isDate(element)) {
        return 'date';

    } else if (isNumber(element)) {
        return 'number';

    } else {
        return 'invalid';
    }
}

const extractMonetaryValue = (value) => {
    const sanitizedValue = value.trim().replace('R$', '').replace(/[^\d.,-]/g, '');
    return sanitizedValue;
};

export function parseNumber(value) {
    if (isNaN(value)) {
        return parseFloat(value.replace(/\./g, '').replace(',', '.'));
    }
    
    const result = parseFloat(value)
    return result
}

function hourToNumber(hour) {
    const [hours, minutes] = hour.split(':').map(Number);
    return hours + minutes / 60;
}

function allSameType(elements) {
    if (elements.length === 0) return 'empty';

    const firstType = determineType(elements[0]);
    if (firstType === 'invalid') return false;

    for (let element of elements) {
        if (determineType(element) !== firstType) {
            return false;
        }
    }
    return firstType;
}

export function sum(elements, operation = 'sum') {
    const type = allSameType(elements);
    if (type === 'error' || type === 'empty') {
        throw new Error("Elements are not of the same type or list is empty");
    }

    switch (type) {
        case 'number':
            return sumNumbers(elements, operation);
        case 'monetary':
            return sumMonetary(elements, operation);
        case 'hour':
            return sumHours(elements, operation);
        default:
            throw new Error("Unsupported element type");
    }
}

function sumNumbers(elements, operation) {
    let result;
    if (operation === 'subtract') {
        result = elements.slice(1).reduce((acc, val) => acc - parseNumber(val), parseNumber(elements[0]));
    } else {
        result = elements.reduce((acc, val) => acc + parseNumber(val), 0);
    }
    return result.toString().replace('.', ',');
}

function sumMonetary(elements, operation) {
    if (operation === 'subtract') {
        const initial = parseNumber(extractMonetaryValue(elements[0]));
        const total = elements.slice(1).reduce((acc, val) => {
            const normalizedValue = parseNumber(extractMonetaryValue(val));
            return acc - normalizedValue;
        }, initial);
        
        return formatMonetary(total.toString().replace('.', ','))
    }

    const total = elements.reduce((acc, val) => {
        const normalizedValue = parseNumber(extractMonetaryValue(val));
        return acc + normalizedValue;
    }, 0);

    const retorno = formatMonetary(total.toString().replace('.', ','))
    return retorno
}

function sumHours(elements, operation) {
    if (operation === 'subtract') {
        const [initialHours, initialMinutes] = elements[0].split(':').map(Number);
        let totalMinutes = initialHours * 60 + initialMinutes;
        totalMinutes = elements.slice(1).reduce((acc, val) => {
            const [hours, minutes] = val.split(':').map(Number);
            const totalMinutesForValue = hours * 60 + minutes;
            return acc - totalMinutesForValue;
        }, totalMinutes);

        const hours = Math.floor(Math.abs(totalMinutes) / 60);
        const minutes = Math.abs(totalMinutes) % 60;
        const sign = totalMinutes < 0 ? '-' : '';
        return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    }

    let totalMinutes = elements.reduce((acc, val) => {
        const [hours, minutes] = val.split(':').map(Number);
        const totalMinutesForValue = hours * 60 + minutes;
        return acc + totalMinutesForValue;
    }, 0);

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

export function multiplication(elements) {
    return processElements(elements, 'multiplication');
}

export function division(elements) {
    if (elements.length < 2) {
        throw new Error('São necessários pelo menos dois valores para a divisão.');
    }
    const result = processElements(elements, 'division');
    return result
}

function convertTo(result, type) {
    switch (type) {
        case 'number':
            return parseFloat(result).toString().replace('.', ',');

        case 'monetary':
            return formatMonetary(parseFloat(result).toString().replace('.', ','))

        case 'hour':
            const totalMinutes = result * 60;
            const hours = Math.floor(totalMinutes / 60);
            const minutes = Math.round(totalMinutes % 60);
            return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

        default:
            throw new Error('Tipo de resultado desconhecido.');
    }
}

function processElements(elements, operation) {
    let numNumbers = 0;
    let numMonetaries = 0;
    let numHours = 0;
    let result = operation === 'multiplication' ? 1 : undefined;

    elements.forEach((element, index) => {
        let parsedValue;

        if (isNumber(element)) {
            numNumbers++;
            parsedValue = parseNumber(element);
        } else if (isMonetary(element)) {
            numMonetaries++;
            parsedValue = parseNumber(element.replace(/[^\d.,]/g, ''));
        } else if (isHour(element)) {
            numHours++;
            parsedValue = hourToNumber(element);
        } else {
            throw new Error('Parâmetro inválido: deve ser um número, valor monetário (ex: "R$ 12,00") ou hora (ex: "12:30").');
        }

        if (numMonetaries > 1) throw new Error('Não pode ter mais de 1 valor monetário.');
        if (numHours > 1) throw new Error('Não pode ter mais de 1 valor de hora.');

        if (operation === 'multiplication') {
            result *= parsedValue;
        } else if (operation === 'division') {
            // Se o primeiro elemento for 0, o resultado é 0
            if (index === 0 && parsedValue === 0) {
                result = 0;
                return;
            }

            if (parsedValue === 0) {
                throw new Error('Divisão por zero não permitida.');
            }

            result = (result === undefined) ? parsedValue : result / parsedValue;
        }
    });

    const type = numMonetaries === 1 ? 'monetary' : numHours === 1 ? "hour" : "number";
    return convertTo(result || 0, type);
}


export const getDifferenceInDays = (date1, date2) => {
    if (!isDate(date1) || !isDate(date2)) return ''

    // Converte as datas em objetos Date
    const d1 = new Date(date1);
    const d2 = new Date(date2);

    // Calcula a diferença em milissegundos
    const diffInMs = d2 - d1;

    // Converte a diferença de milissegundos para dias
    const diffInDays = Math.round(diffInMs / (1000 * 60 * 60 * 24));

    // Retorna a diferença em dias
    return diffInDays || "0";
};

const composta = (base,  multiplicador, quantidade, divisor) => {
    if (isDate(base) || isDate(divisor) || isDate(multiplicador) || isDate(quantidade)) return ''

    let result = multiplication([base,  multiplicador, quantidade])
    result = division([result, divisor])
    return result || "0";
};

export const functions = {
    sum: {
        name: "Soma",
        manyParams: true,
        minParams: 2,
        typesPermitted: ["monetary", "number", "hours"],
        execute: (params) => sum(params, "sum"),
    },
    subtract: {
        name: "Subtração",
        manyParams: true,
        minParams: 2,
        typesPermitted: ["monetary", "number", "hours"],
        execute: (params) => sum(params, "subtract"),
    },
    division: {
        name: "Divisão",
        manyParams: true,
        minParams: 2,
        typesPermitted: ["monetary", "number", "hours"],
        execute: (params) => division(params),
    },
    multiplication: {
        name: "Multiplicação",
        manyParams: true,
        minParams: 2,
        typesPermitted: ["monetary", "number", "hours"],
        execute: (params) => multiplication(params),
    },
    dateDifference: {
        name: "Diferença entre datas",
        manyParams: false,
        minParams: 2,
        params: [{name: "data inicial"}, {name: "data final"}],
        typesPermitted: ["date"],
        execute: (params) => getDifferenceInDays(params[0], params[1]),
    },
    composta: {
        name: "Composta",
        manyParams: false,
        minParams: 4,
        params: [
            {name: "base"},
            {name: "multiplicador"},
            {name: "quantidade"},
            {name: "divisor"}, 
        ],
        typesPermitted: ["monetary", "number", "hours"],
        execute: (params) => composta(params[0], params[1], params[2], params[3]),
    },
}
